import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { IconButton, Image, Spinner } from 'components';
import { getLayout } from 'components/layouts/Header';
import { Row } from 'components/layout';
import ActionMenu, { ActionOption } from 'components/ActionMenu';
import { Toggle } from 'components/form';
import DatePosted from 'components/DatePosted';
import { useResponsive } from 'hooks/useResponsive';
import { useCandidate } from 'hooks/useCandidate';
import { useNotification } from 'hooks/useNotification';

import { parseArrayResponse } from 'lib/utils/parser';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import { withAuthCandidate } from 'lib/hocs/withAuthCandidate';
import { LOCALE_PAGES, fetchTranslationFiles } from 'lib/services/page';
import { CandidateNotificationResponse } from 'lib/models/candidate';
import styles from './Notifications.module.scss';

const Notifications = (): ReactElement => {
  const router = useRouter();
  const screens = useResponsive();
  const [t] = useTranslation('candidate.notification');
  const {
    getUserNotification,
    readAllCandidateNotifications,
    toggleCandidateNotification,
  } = useCandidate();
  const { candidate, updateUserApiResponse } = useAuth();
  const { notifications_enabled } = candidate || {};
  const notificationInstance = useNotification();
  const { refetchCandidateDashboard, candidateDashboard } =
    useCandidateDashboard();
  const unreadNotifications = candidateDashboard?.unread_notifications_count;

  const [isEnabledNotification, setEnabledNotification] = useState(false);
  const [userNotificationResponse, setNotificationResponse] =
    useState<CandidateNotificationResponse>();

  useEffect(() => {
    const fetchUserNotificationResponse = async () => {
      const response = await getUserNotification();
      setNotificationResponse(response);
    };
    fetchUserNotificationResponse();
  }, []);

  useEffect(() => {
    if (unreadNotifications && notifications_enabled === false) {
      clearAllNotificationsHandler();
    }
  }, [unreadNotifications, notifications_enabled]);

  const userNotification = useMemo(() => {
    return parseArrayResponse(userNotificationResponse);
  }, [userNotificationResponse]);

  useEffect(() => {
    setEnabledNotification(notifications_enabled!);
  }, [notifications_enabled]);

  const onChangeNotificationHandler = async () => {
    try {
      const response = await toggleCandidateNotification(
        !isEnabledNotification
      );
      if (response) {
        if (!isAllNotificationRead && isEnabledNotification)
          clearAllNotificationsHandler();
        updateUserApiResponse(response);
      }
    } catch (error) {
      notificationInstance.handleExceptionError(error);
    }
  };

  const clearAllNotificationsHandler = async () => {
    try {
      const readResponse = await readAllCandidateNotifications();
      if (readResponse) {
        refetchCandidateDashboard();
        const response = await getUserNotification();
        setNotificationResponse(response);
      }
    } catch (error) {
      notificationInstance.handleExceptionError(error);
    }
  };

  //Check if is_read on all notifications is true
  const isAllNotificationRead = userNotification?.every(
    ({ is_read }) => is_read === true
  );

  const notificationCards = useMemo(() => {
    return userNotification?.map(
      ({ id, created_at, title, value, icon_url, is_read }) => {
        return (
          <div key={id} className={styles.card}>
            <div className={styles.imageContainer}>
              <Image src={icon_url} className={styles.logo} />
              {!is_read && <div className={styles.dot}></div>}
            </div>
            <div>
              <p className={styles.value}>{value}</p>
              <p className={styles.desc}>{title}</p>
              <DatePosted className={styles.date} created_at={created_at} />
            </div>
          </div>
        );
      }
    );
  }, [userNotification]);

  const menuOptions = [
    {
      id: '1',
      label: t('menu.notifications'),
      actionElement: (
        <Toggle
          checked={isEnabledNotification}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onChange={() => {
            setEnabledNotification((flag) => !flag);
            onChangeNotificationHandler();
          }}
        />
      ),
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
      },
    },
    ...(!isAllNotificationRead && isEnabledNotification
      ? [
          {
            id: '2',
            label: t('menu.clear-all'),
            icon: 'trash',
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              clearAllNotificationsHandler();
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <Head>
        <title>Notifications | Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>
      <section className={styles.Notifications} data-testid="Notifications">
        <main className={styles.section}>
          <Row align="center" justify="space-between" className={styles.header}>
            {screens.sm ? (
              <IconButton
                iconName="chevron-left"
                size="xxlarge"
                onClick={() => router.push(PAGE_ROUTES.CANDIDATE_SETTINGS)}
              />
            ) : null}
            <p className={styles.title}>{t('title')}</p>
            <ActionMenu
              options={menuOptions as Array<ActionOption>}
              className={styles.menu}
            />
          </Row>
          {!userNotificationResponse ? (
            <div className={styles.spinner}>
              <Spinner />
            </div>
          ) : isAllNotificationRead || !isEnabledNotification ? (
            <>
              <div className={styles.nodata}>
                <p className={styles.nodataEmoji}>🙁</p>
                <p className={styles.nodataTitle}>
                  {t('no-more-notifications')}
                </p>
              </div>
            </>
          ) : (
            notificationCards
          )}
        </main>
      </section>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await fetchTranslationFiles({
        locale,
        pages: [LOCALE_PAGES.CANDIDATE_NOTIFICATIONS],
      })),
    },
  };
}

const NotificationsWrapper = withAuthCandidate(Notifications);
// @ts-ignore
NotificationsWrapper.getLayout = getLayout;

export default NotificationsWrapper;
